/**
 * Task Portal Service Hooks
 * React hooks for accessing task portal services (task creator/assignee self-service)
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all task portal services
 * @returns TaskPortalServices object containing all task portal service instances
 */
export function useTaskPortalServices() {
    const { services } = useCCMS();
    if (!services?.taskPortal) {
        throw new Error('Task Portal services not available. Ensure CCMSProvider is configured with taskPortal services.');
    }
    return services.taskPortal;
}
/**
 * Hook to access TaskPortalTaskService
 * Used for task operations (list, get, update, delete)
 * - Creators: full CRUD
 * - Assignees: view, update status/progress
 */
export function useTaskPortalTaskService() {
    return useTaskPortalServices().tasks;
}
/**
 * Hook to access TaskPortalCommentService
 * Used for task comment management
 * - Creators: full CRUD on all comments
 * - Assignees: create, edit/delete own comments (30-min window)
 */
export function useTaskPortalCommentService() {
    return useTaskPortalServices().comments;
}
/**
 * Hook to access TaskPortalNoteService
 * Used for task note management
 * - Creators: full CRUD on all notes
 * - Assignees: CRUD on own notes only
 */
export function useTaskPortalNoteService() {
    return useTaskPortalServices().notes;
}
/**
 * Hook to access TaskPortalAttachmentService
 * Used for task attachment management
 * - Creators: full CRUD on all attachments
 * - Assignees: create, edit/delete own attachments
 */
export function useTaskPortalAttachmentService() {
    return useTaskPortalServices().attachments;
}
/**
 * Hook to access TaskPortalNotificationService
 * Used for viewing and managing task notifications
 * - Both creators and assignees: view and mark as read
 */
export function useTaskPortalNotificationService() {
    return useTaskPortalServices().notifications;
}
/**
 * Hook to access TaskPortalAssignmentService
 * Used for task assignment management
 * - Creators only: assign/remove users
 * - Assignees: view only
 */
export function useTaskPortalAssignmentService() {
    return useTaskPortalServices().assignments;
}
/**
 * Hook to access TaskPortalDashboardService
 * Used for dashboard statistics, unseen tasks, in-progress tasks
 */
export function useTaskPortalDashboardService() {
    return useTaskPortalServices().dashboard;
}
/**
 * Hook to access TaskPortalProjectService
 * Used for project summaries and tasks by project
 */
export function useTaskPortalProjectService() {
    return useTaskPortalServices().projects;
}
/**
 * Hook to access TaskPortalActivityFeedService
 * Used by the activity drawer: aggregated comments/notes across the user's tasks,
 * per-user read receipts, and the @-mention candidate list for a task.
 */
export function useTaskPortalActivityFeedService() {
    return useTaskPortalServices().activityFeed;
}
//# sourceMappingURL=useTaskPortalServices.js.map